import { graphql } from 'gatsby';
import * as React from 'react';

import GetQuoteBanner from '_components/article-v3/GetQuoteBanner';
import OtherArticles from '_components/article-v3/OtherArticles';
import ArticlePortableText from '_components/ArticlePortableText';
import Breadcrumb from '_components/BreadCrumb';
import GetQuoteCard from '_components/GetQuoteCard';
import Layout from '_components/layout';
import SEO from '_components/seo_v3';
import Tag from '_components/tag';
import { urlForArticleCategory } from '_utils/articles';
import { urlFor } from '_utils/images';
import { createArticleData, createBreadcrumbData } from '_utils/structuredData';

import ArticleAds from './ArticleAds';
import ArticleHead from './ArticleHead';
import RelatedArticles from './RelatedArticles';
import SimilarCategoryCars from './SimilarCategoryCars';

import useTailwindBreakpoint from '_utils/useTailwindBreakpoint';
import Ads from '_components/Ads';

const domain = process.env.GATSBY_WEB_DOMAIN;

/**
 * Convert Sanity blocks to plain text
 * @param {Array} blocks - Sanity content blocks
 * @returns {string} Plain text content
 */
const blocksToText = (blocks = []) => {
  return blocks
    .map(block => {
      if (block._type !== 'block' || !block.children) {
        return '';
      }
      return block.children.map(child => child.text).join('');
    })
    .join('\n\n');
};

function Article({ data }) {
  const {
    sanityArticle: article,
    sameCategoryArticles,
    sameMakeArticles,
  } = data;

  const tailwindBreakpoint = useTailwindBreakpoint()

  return (
    <Layout
      leadFormType={article.leadForm}
      headerProps={{ activeKey: '/articles' }}
      v3
      showArticleSearchBar
    >
      <main className="w-full max-w-[1440px] mx-auto px-4 md:px-8 xl:px-16">
        <Breadcrumb
          className="my-6 sm:my-8"
          items={[
            { label: 'Home', url: '/' },
            { label: 'Articles', url: '/articles' },
            {
              label: article.category_v3?.title,
              url: urlForArticleCategory(article.category_v3),
            },
            { label: article.title, current: true },
          ]}
        />
        <div className="w-full flex flex-col lg:flex-row gap-x-8 text-content-primary">
          <div className="flex-1 max-w-[976px] lg:min-w-0">
            <div className="p-0 sm:p-8 rounded-lg sm:shadow-card bg-white">
              <ArticleHead article={article} />
              <section className="mt-10 text-base leading-6">
                <ArticlePortableText content={article._rawContent} />
                <div className="tag-list-wrapper">
                  {article.tags_v3.map((tag, index) => (
                    <Tag key={index}>{tag.name}</Tag>
                  ))}
                </div>
              </section>

              <div className="my-8 px-4 md:px-0">
                <div className="w-[320px] md:w-[728px] mx-auto">
                  <Ads
                    desktop={{
                      adsId: '/325540922/oneshift_web/desktop_article_detail_leaderboard_btf_01',
                      sizes: [[728, 90], 'fluid'],
                    }}
                    mobile={{
                      adsId: '/325540922/oneshift_web/mobile_article_detail_leaderboard_btf_01',
                      sizes: [[320, 100], 'fluid'],
                    }}
                    divId="div-gpt-ad-article-detail-leaderboard-btf"
                  />
                </div>
              </div>
              
              {article.category_v3?.slug.current === 'car-reviews' &&
              sameMakeArticles.edges.length > 0 ? (
                <SimilarCategoryCars
                  className="mt-16 w-full"
                  articles={sameMakeArticles.edges.map((e) => e.node)}
                />
              ) : null}
            </div>
            <GetQuoteBanner className="mt-16" utmMedium="article-detail" />
            <OtherArticles />
          </div>
          <section className="w-full lg:w-[300px]">
            <ArticleAds />
            <GetQuoteCard className="mx-auto" utmMedium="article-detail" />
            <RelatedArticles
              articles={sameCategoryArticles.edges.map((e) => e.node)}
            />
          </section>
        </div>
      </main>
    </Layout>
  );
}

export default Article;

export const Head = ({ location, params, data }) => {
  const { title, thumbnail, excerpt, publishDate, author, category_v3, _rawContent } = data.sanityArticle;
  const seoTitle = `${title} | OneShift by Carousell`;
  const imageUrl = urlFor(thumbnail).format('jpg').width(1200).url();

  const structuredDataWithoutUrl = (siteUrl) => [
    createArticleData({
      title,
      description: excerpt,
      image: imageUrl,
      publishDate,
      author,
      path: location.pathname,
      body: _rawContent ? blocksToText(_rawContent) : excerpt
    }, siteUrl),
    createBreadcrumbData([
      { name: "Home", path: "/" },
      { name: "Articles", path: "/articles" },
      { name: category_v3?.title || "Article", path: category_v3?.slug?.current ? `/articles/${category_v3.slug.current}` : "/articles" },
      { name: title, path: location.pathname }
    ], siteUrl)
  ];

  return (
    <SEO 
      title={seoTitle} 
      description={excerpt} 
      imageUrl={imageUrl} 
      pathname={location.pathname} 
      ogType="article"
      getStructuredData={structuredDataWithoutUrl}
    />
  );
};

export const query = graphql`
  query ($articleId: String, $categoryId: String, $makeId: String) {
    site {
      siteMetadata {
        title
        description
        author
      }
    }
    sanityArticle(_id: { eq: $articleId }) {
      _id
      id
      title
      excerpt
      publishDate
      category_v3 {
        title
        slug {
          current
        }
      }
      slug {
        current
      }
      thumbnail {
        asset {
          id
          title
          description
          label
          url
          width
          height
        }
      }
      images {
        asset {
          id
          title
          description
          label
          url
          width
          height
        }
      }
      author {
        name
        profileImage {
          asset {
            id
            title
            description
            label
            url
            width
            height
          }
        }
      }
      tags
      tags_v3 {
        name
        key {
          current
        }
      }
      make {
        name
        logo {
          asset {
            url
          }
        }
      }
      leadForm
      relatedVariants {
        _id
        id
        name
        year
        price
        availability
        model {
          name
          dealerType
          make {
            _id
            name
          }
        }
        thumbnail {
          asset {
            id
            url
            width
            height
          }
        }
        slug {
          current
        }
        carOverview {
          fuelConsumption
          fuelType
          bodyType
        }
      }
      _rawContent
    }

    sameCategoryArticles: allSanityArticle(
      sort: { publishDate: DESC }
      filter: {
        _id: { ne: $articleId }
        category: { _id: { eq: $categoryId } }
        hidden: { eq: false }
      }
      limit: 3
    ) {
      edges {
        node {
          _id
          id
          title
          slug {
            current
          }
          category_v3 {
            id
            title
            slug {
              current
            }
          }
          excerpt
          tags
          tags_v3 {
            name
            key {
              current
            }
          }
          thumbnail {
            asset {
              id
              title
              description
              label
              url
              width
              height
            }
          }
        }
      }
    }

    sameMakeArticles: allSanityArticle(
      sort: { publishDate: DESC }
      filter: {
        _id: { ne: $articleId }
        make: { _id: { eq: $makeId } }
        hidden: { eq: false }
      }
      limit: 6
    ) {
      edges {
        node {
          _id
          id
          title
          slug {
            current
          }
          category_v3 {
            id
            title
            slug {
              current
            }
          }
          excerpt
          tags
          tags_v3 {
            name
            key {
              current
            }
          }
          thumbnail {
            asset {
              id
              title
              description
              label
              url
              width
              height
            }
          }
        }
      }
    }
  }
`;
